<template>
  <v-dialog
    v-if="featureEnabled"
    :value="open"
    persistent
    width="480"
    @keydown.esc="handleCancel"
  >
    <v-card class="ejfd-card pa-6 background-pattern-icon" data-testid="edit-job-family-dialog">
      <div class="d-flex align-center mb-4">
        <span class="ejfd-featured-icon" aria-hidden="true">
          <svg
            class="ejfd-layers-icon"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="m22 12.7-8.4 3.82a2 2 0 0 1-1.6 0L3.6 12.7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="m22 17.7-8.4 3.82a2 2 0 0 1-1.6 0L3.6 17.7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <v-spacer />
        <button
          type="button"
          class="ejfd-close"
          aria-label="Close Edit Job Family dialog"
          data-testid="ejfd-close"
          @click="handleCancel"
        >
          <svg
            class="ejfd-close__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <h2 id="ejfd-title" class="ejfd-title" data-testid="ejfd-title">Edit Job Family</h2>

      <div class="ejfd-field">
        <label class="ejfd-label" for="ejfd-name-input" data-testid="ejfd-name-label">
          Name
          <span class="ejfd-required" aria-hidden="true">*</span>
        </label>
        <input
          id="ejfd-name-input"
          v-model.trim="localName"
          class="ejfd-input"
          :class="{ 'ejfd-input--filled': hasName, 'ejfd-input--error': Boolean(nameError) }"
          type="text"
          placeholder="e.g. Engineering"
          autocomplete="off"
          required
          :aria-required="true"
          :aria-invalid="Boolean(nameError)"
          :disabled="busy"
          data-testid="ejfd-name-input"
        />
        <p v-if="nameError" class="ejfd-error" role="alert" data-testid="ejfd-name-error">
          {{ nameError }}
        </p>
      </div>

      <div class="ejfd-actions">
        <button
          type="button"
          class="ejfd-btn ejfd-btn--secondary"
          :disabled="busy"
          data-testid="ejfd-cancel"
          @click="handleCancel"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ejfd-btn ejfd-btn--primary"
          :disabled="!canSave"
          data-testid="ejfd-save"
          @click="handleSave"
        >
          {{ busy ? "Saving..." : "Save" }}
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import { useOrgObjectNameValidation } from "@/composables/useOrgObjectNameValidation";

const props = defineProps({
  open: { type: Boolean, default: false },
  name: { type: String, default: "" },
  familyId: { type: [String, Number], default: null },
  busy: { type: Boolean, default: false },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update:open", "save", "cancel"]);

const { proxy } = getCurrentInstance() || {};
const featureEnabled = ref(true);
const localName = ref("");

const nameValidation = useOrgObjectNameValidation({
  type: "job_family",
  excludeId: () => props.familyId,
});

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      localName.value = String(props.name || "").trim();
      nameValidation.error.value = "";
    }
  },
  { immediate: true }
);

watch(
  () => props.name,
  (value) => {
    if (props.open) {
      localName.value = String(value || "").trim();
    }
  }
);

watch(localName, (value) => {
  nameValidation.name.value = value === String(props.name || "").trim() ? "" : value;
});

const hasName = computed(() => Boolean(localName.value));
const nameError = computed(() => props.errors?.name || nameValidation.error.value);
// Only an identical (trimmed) name is a no-op; a case-only rename is a real change.
const canSave = computed(
  () =>
    hasName.value &&
    !props.busy &&
    !nameValidation.checking.value &&
    !nameValidation.error.value &&
    localName.value !== String(props.name || "").trim()
);

function handleCancel() {
  emit("update:open", false);
  emit("cancel");
}

function handleSave() {
  if (!canSave.value) return;
  emit("save", { name: localName.value });
}
</script>

<style scoped>
.ejfd-title {
  margin: 0 0 20px;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.ejfd-featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #f9f5ff;
  color: #6941c6;
}

.ejfd-close {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  color: #667085;
}

.ejfd-field {
  margin-bottom: 24px;
}

.ejfd-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}

.ejfd-required {
  color: #d92d20;
}

.ejfd-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 16px;
  line-height: 24px;
  box-sizing: border-box;
}

.ejfd-input--error {
  border-color: #fda29b;
}

.ejfd-error {
  margin: 6px 0 0;
  font-size: 14px;
  color: #d92d20;
}

.ejfd-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.ejfd-btn {
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ejfd-btn--secondary {
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #344054;
}

.ejfd-btn--primary {
  border: none;
  background: #7f56d9;
  color: #fff;
}

.ejfd-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
